import React from "react";
import { useRecoilValue } from "recoil";
import { recoilStates } from "src/recoil/recoilStates";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import styled from "styled-components";

// styled-components로 마크다운 스타일링
const StyledH1 = styled.h1`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
  color: #1a1a1a;
`;

const StyledH2 = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #374151;
`;

const StyledH3 = styled.h3`
  font-size: 1.125rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #4b5563;
`;

const StyledP = styled.p`
  margin-bottom: 0.5rem;
  color: #374151;
  line-height: 1.6;
`;

const StyledUl = styled.ul`
  list-style-type: disc;
  margin-left: 1.25rem;
  margin-bottom: 0.75rem;
`;

const StyledOl = styled.ol`
  list-style-type: decimal;
  margin-left: 1.25rem;
  margin-bottom: 0.75rem;
`;

const StyledLi = styled.li`
  margin-bottom: 0.25rem;
  color: #374151;
`;

const StyledStrong = styled.strong`
  font-weight: bold;
  color: #1a1a1a;
`;

const StyledEm = styled.em`
  font-style: italic;
  color: #374151;
`;

const MarkdownContainer = styled.div`
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.6;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }

  p {
    margin-bottom: 0.75rem;
  }

  ul,
  ol {
    margin-bottom: 0.75rem;
  }

  li {
    margin-bottom: 0.25rem;
  }
`;

const ModalContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  width: 100%;
  overflow-y: auto;
  padding: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: bold;
  color: #1a1a1a;
  margin-bottom: 16px;
`;

const InfoTable = styled.div`
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;
`;

const InfoRow = styled.div`
  display: flex;
  border-bottom: 1px solid #d1d5db;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.div`
  width: 100px;
  padding: 12px;
  background-color: #f9fafb;
  border-right: 1px solid #d1d5db;
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
`;

const InfoValue = styled.div`
  flex: 1;
  padding: 12px;
  font-size: 0.875rem;
  color: #374151;

  a {
    color: #2563eb;
    text-decoration: underline;
    word-break: break-all;
  }
`;

const PolicySection = styled.div`
  margin-bottom: 24px;
`;

const PolicyTitle = styled.h3`
  font-size: 1rem;
  font-weight: bold;
  color: #1a1a1a;
  margin-bottom: 12px;
`;

const PolicyContent = styled.div`
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.6;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: #e5e7eb;
  margin: 16px 0;
`;

// 마크다운 컴포넌트
const markdownComponents = {
  h1: ({ children }: any) => <StyledH1>{children}</StyledH1>,
  h2: ({ children }: any) => <StyledH2>{children}</StyledH2>,
  h3: ({ children }: any) => <StyledH3>{children}</StyledH3>,
  p: ({ children }: any) => <StyledP>{children}</StyledP>,
  ul: ({ children }: any) => <StyledUl>{children}</StyledUl>,
  ol: ({ children }: any) => <StyledOl>{children}</StyledOl>,
  li: ({ children }: any) => <StyledLi>{children}</StyledLi>,
  strong: ({ children }: any) => <StyledStrong>{children}</StyledStrong>,
  em: ({ children }: any) => <StyledEm>{children}</StyledEm>,
};

export default function CancellationPolicy() {
  const bookingApplication = useRecoilValue(recoilStates.bookingApplication);

  // 확정된 골프장 정보 가져오기 (status_id가 4 또는 6인 경우)
  const clubRelData = bookingApplication?.clubRels?.find((cr) =>
    [4, 6].includes(cr.year_booking_status_id)
  );
  const clubInfo = clubRelData?.club?.[0];
  const rules = clubInfo?.rules;

  // ✅ 이스케이프된 개행(\n) → 실제 줄바꿈으로 변환
  const normalizedRules = rules?.replace(/\\n/g, "\n") ?? "";

  return (
    <ModalContainer>
      <ModalContent>
        {/* 골프장 정보 섹션 */}
        {clubInfo && (
          <PolicySection>
            <SectionTitle>골프장 정보</SectionTitle>
            <InfoTable>
              <InfoRow>
                <InfoLabel>골프장명</InfoLabel>
                <InfoValue>{clubInfo.name}</InfoValue>
              </InfoRow>
              {clubInfo.region && (
                <InfoRow>
                  <InfoLabel>지역</InfoLabel>
                  <InfoValue>{clubInfo.region}</InfoValue>
                </InfoRow>
              )}
              {(clubInfo.homepage || clubInfo.webpage) && (
                <InfoRow>
                  <InfoLabel>웹사이트</InfoLabel>
                  <InfoValue>
                    <a
                      href={clubInfo.homepage || clubInfo.webpage}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {clubInfo.homepage || clubInfo.webpage}
                    </a>
                  </InfoValue>
                </InfoRow>
              )}
            </InfoTable>
          </PolicySection>
        )}

        {/* 골프장 취소/위약규정 */}
        {normalizedRules && (
          <PolicySection>
            <Divider />
            <PolicyContent>
              <MarkdownContainer>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={markdownComponents}
                >
                  {normalizedRules}
                </ReactMarkdown>
              </MarkdownContainer>
            </PolicyContent>
          </PolicySection>
        )}

        {/* 골프장별 취소/위약 규정이 없는 경우 */}
        {!rules && (
          <PolicySection>
            <PolicyTitle>취소/위약 규정</PolicyTitle>
            <PolicyContent>
              <p>해당 골프장의 취소/위약 규정 정보가 없습니다.</p>
            </PolicyContent>
          </PolicySection>
        )}
      </ModalContent>
    </ModalContainer>
  );
}
